import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  UserPlus, 
  MessageCircle, 
  Heart, 
  Share2, 
  Flag, 
  Users,
  Info
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { userConnectionsService } from '@/services/userConnectionsService';
import { UserProfileData } from '@/types/userProfile';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface SocialActionsProps {
  profile: UserProfileData;
  viewAsPublic?: boolean;
}

export const SocialActions = ({ profile, viewAsPublic = false }: SocialActionsProps) => {
  console.log('🔴 SOCIAL ACTIONS COMPONENT RENDERING');
  console.log('🔴 Profile received:', profile);
  
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  
  const currentUser = supabaseUser || internalUser;
  
  console.log('🔴 Auth Check - Supabase User:', supabaseUser);
  console.log('🔴 Auth Check - Internal User:', internalUser);
  console.log('🔴 Auth Check - Current User:', currentUser);
  console.log('🔴 Profile ID Check:', profile.id);
  console.log('🔴 Is Own Profile:', currentUser?.id === profile.id);
  
  const isOwnProfile = currentUser?.id === profile.id && !viewAsPublic;
  const isActualOwnProfile = currentUser?.id === profile.id;
  
  const [connectionStatus, setConnectionStatus] = useState<'none' | 'pending' | 'connected' | 'following'>('none');
  const [followerCount, setFollowerCount] = useState(0);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Check connection status and load follower count
  useEffect(() => {
    const checkConnectionStatus = async () => {
      if (!currentUser) return;
      
      try {
        // Handle venue profiles differently
        if (profile.role === 'venue') {
          // Check if following venue
          const { data: venueFollow } = await supabase
            .from('venue_followers')
            .select('id')
            .eq('venue_id', profile.id)
            .eq('follower_id', currentUser.id)
            .eq('follower_type', supabaseUser ? 'supabase' : 'internal')
            .eq('is_active', true)
            .maybeSingle();

          setConnectionStatus(venueFollow ? 'following' : 'none');

          // Get venue follower count
          const { count: venueFollowerCount } = await supabase
            .from('venue_followers')
            .select('*', { count: 'exact', head: true })
            .eq('venue_id', profile.id)
            .eq('is_active', true);

          setFollowerCount(venueFollowerCount || 0);
        } else {
          // Regular user profiles - use existing logic
          const connections = await userConnectionsService.getConnectionStatus(currentUser.id, profile.id);
          if (connections.length > 0) {
            const connection = connections[0];
            if (connection.connection_type === 'follow') {
              setConnectionStatus('following');
            } else if (connection.status === 'accepted') {
              setConnectionStatus('connected');
            } else {
              setConnectionStatus('pending');
            }
          }
          
          // Get follower count for regular users
          const allConnections = await userConnectionsService.getConnections(profile.id);
          const followers = allConnections.filter(conn => 
            conn.connection_type === 'follow' && conn.status === 'accepted'
          );
          setFollowerCount(followers.length);
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
      }
    };

    checkConnectionStatus();
  }, [currentUser, profile.id, profile.role, supabaseUser]);

  const canConnect = () => {
    if (!currentUser || isActualOwnProfile) return false;
    
    const allowedRoles = ['coach', 'team-owner', 'venue'];
    const userRole = supabaseUser ? 'supabase-user' : (internalUser?.role || '');
    
    return allowedRoles.includes(profile.role) || allowedRoles.includes(userRole);
  };

  const canMessage = () => {
    if (!currentUser || isActualOwnProfile) return false;
    
    // Define messaging rules based on roles
    const restrictedFromMessaging = ['fan', 'creative'];
    const currentUserRole = supabaseUser ? 'supabase-user' : (internalUser?.role || '');
    
    return !restrictedFromMessaging.includes(currentUserRole);
  };

  const canFollow = () => {
    return !isActualOwnProfile; // Can't follow yourself
  };

  const canReport = () => {
    return !isActualOwnProfile; // Can't report yourself
  };

  const handleConnect = async () => {
    if (!currentUser) {
      toast.error('Please log in to connect');
      return;
    }

    try {
      const userType = supabaseUser ? 'supabase' : 'internal';
      const connectedUserType = profile.isInternal ? 'internal' : 'supabase';
      
      await userConnectionsService.createConnection(
        currentUser.id,
        profile.id,
        'connect',
        userType,
        connectedUserType
      );
      
      setConnectionStatus('pending');
      toast.success('Connection request sent!');
    } catch (error) {
      console.error('Error creating connection:', error);
      toast.error('Failed to send connection request');
    }
  };

  const handleFollow = () => {
    if (isActualOwnProfile) {
      toast.info("You can't follow yourself!");
      return;
    }
    
    if (!currentUser) {
      toast.error('Please log in to follow');
      return;
    }

    handleFollowAsync();
  };

  const handleFollowAsync = async () => {
    if (!currentUser) return;
    
    try {
      const userType = supabaseUser ? 'supabase' : 'internal';
      
      if (profile.role === 'venue') {
        // Handle venue following
        if (connectionStatus === 'following') {
          // Unfollow venue
          const { error } = await supabase
            .from('venue_followers')
            .update({ is_active: false })
            .eq('venue_id', profile.id)
            .eq('follower_id', currentUser.id)
            .eq('follower_type', userType);

          if (error) throw error;
          
          setConnectionStatus('none');
          setFollowerCount(prev => Math.max(0, prev - 1));
          toast.success('Unfollowed venue');
        } else {
          // Follow venue
          const { error } = await supabase
            .from('venue_followers')
            .upsert({
              venue_id: profile.id,
              follower_id: currentUser.id,
              follower_type: userType,
              is_active: true
            }, {
              onConflict: 'venue_id,follower_id'
            });

          if (error) throw error;
          
          setConnectionStatus('following');
          setFollowerCount(prev => prev + 1);
          toast.success('Following venue');
        }
      } else {
        // Regular user following logic
        const connectedUserType = profile.isInternal ? 'internal' : 'supabase';
        
        if (connectionStatus === 'following') {
          await userConnectionsService.removeConnection(currentUser.id, profile.id, 'follow');
          setConnectionStatus('none');
          setFollowerCount(prev => prev - 1);
          toast.success('Unfollowed user');
        } else {
          await userConnectionsService.createConnection(
            currentUser.id,
            profile.id,
            'follow',
            userType,
            connectedUserType
          );
          setConnectionStatus('following');
          setFollowerCount(prev => prev + 1);
          toast.success('Following user!');
        }
      }
    } catch (error) {
      console.error('Error updating follow status:', error);
      toast.error('Failed to update follow status');
    }
  };

  const handleMessage = () => {
    if (!canMessage()) {
      toast.error('You cannot message this user type');
      return;
    }
    
    // Navigate to chat with this user
    navigate('/chat', { state: { startChat: profile.id } });
  };

  const handleShare = async () => {
    const profileUrl = `${window.location.origin}/${profile.username || profile.slug}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.first_name} ${profile.last_name}'s Profile`,
          text: `Check out ${profile.first_name}'s profile!`,
          url: profileUrl,
        });
      } catch (error) {
        // Fallback to clipboard
        await navigator.clipboard.writeText(profileUrl);
        toast.success('Profile link copied to clipboard!');
      }
    } else {
      await navigator.clipboard.writeText(profileUrl);
      toast.success('Profile link copied to clipboard!');
    }
  };

  const handleReport = async () => {
    if (!currentUser) {
      toast.error('Please log in to report');
      return;
    }

    if (!reportType || !reportDescription.trim()) {
      toast.error('Please select a report type and provide a description');
      return;
    }

    setIsSubmittingReport(true);
    
    try {
      // Submit report to admin dashboard using Supabase
      const { reportService } = await import('@/services/reportService');
      await reportService.submitUserReport({
        reported_user_id: profile.id,
        reported_by: currentUser.id,
        report_type: reportType,
        description: reportDescription,
        evidence: {
          profile_url: window.location.href,
          timestamp: new Date().toISOString()
        }
      });
      
      toast.success('Report submitted successfully');
      setIsReportModalOpen(false);
      setReportType('');
      setReportDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const showConnectInfoDialog = () => {
    const currentUserRole = supabaseUser ? 'supabase-user' : (internalUser?.role || '');
    let message = '';
    
    if (currentUserRole === 'fan') {
      message = 'As a fan, you cannot connect with other users. However, you can follow users to stay updated with their activities!';
    } else if (currentUserRole === 'creative') {
      message = 'As a creative, you cannot connect with other users. Please try the chat feature instead to communicate with potential collaborators.';
    } else {
      message = 'You do not have permission to connect with this user type.';
    }
    
    toast.info(message);
  };

  // Use the already defined isActualOwnProfile
  
  // Hide all social actions if it's actually your own profile and not in public view mode
  if (isActualOwnProfile && !viewAsPublic) {
    console.log('=== HIDING SOCIAL ACTIONS - OWN PROFILE ===');
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <p className="text-gray-400 text-center">Use "View as Public" to see social actions</p>
      </div>
    );
  }

  console.log('=== SHOWING SOCIAL ACTIONS ===');
  console.log('View as public mode:', viewAsPublic);
  console.log('Is actual own profile:', isActualOwnProfile);

  return (
    <div className="space-y-4 mt-6 pt-6 border-t border-gray-700">
      {/* Stats Row - Follower Count (this will be integrated with other stats) */}
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-sports-green" />
        <span className="text-sm text-gray-300">{followerCount} followers</span>
      </div>
      
      {/* Action Buttons Row */}
      <div className="flex flex-wrap gap-3">
        {/* Connect Button */}
        {canConnect() ? (
          <Button
            onClick={handleConnect}
            disabled={connectionStatus === 'pending' || connectionStatus === 'connected'}
            className="bg-sports-green text-black hover:bg-sports-green/80 flex items-center gap-2"
            size="sm"
          >
            <UserPlus className="h-4 w-4" />
            {connectionStatus === 'pending' ? 'Pending' : 
             connectionStatus === 'connected' ? 'Connected' : 'Connect'}
          </Button>
        ) : (
          <Button
            onClick={showConnectInfoDialog}
            variant="outline"
            className="border-sports-green text-sports-green hover:bg-sports-green/10 flex items-center gap-2"
            size="sm"
          >
            <Info className="h-4 w-4" />
            Connect
          </Button>
        )}

        {/* Message Button */}
        {canMessage() ? (
          <Button
            onClick={handleMessage}
            variant="outline"
            className="bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-2"
            size="sm"
          >
            <MessageCircle className="h-4 w-4" />
            Message
          </Button>
        ) : (
          <Button
            onClick={() => toast.info('You cannot message this user type. Visit our FAQ to learn more about messaging permissions.')}
            variant="outline"
            className="border-blue-500 text-blue-400 hover:bg-blue-500/10 flex items-center gap-2"
            size="sm"
          >
            <MessageCircle className="h-4 w-4" />
            Message
          </Button>
        )}

        {/* Follow Button */}
        {canFollow() ? (
          <Button
            onClick={handleFollow}
            variant={connectionStatus === 'following' ? 'default' : 'outline'}
            className={connectionStatus === 'following' 
              ? "bg-red-500 text-white hover:bg-red-600 flex items-center gap-2"
              : "border-sports-green text-sports-green hover:bg-sports-green hover:text-black flex items-center gap-2"
            }
            size="sm"
          >
            <Heart className={connectionStatus === 'following' ? "h-4 w-4 fill-current" : "h-4 w-4"} />
            {connectionStatus === 'following' ? 'Unfollow' : 'Follow'}
          </Button>
        ) : (
          <Button
            onClick={() => toast.info("You can't follow yourself!")}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/10 flex items-center gap-2"
            size="sm"
            disabled
          >
            <Heart className="h-4 w-4" />
            Follow
          </Button>
        )}

        {/* Share Button */}
        <Button
          onClick={handleShare}
          variant="outline"
          className="border-purple-500 text-purple-400 hover:bg-purple-500/10 flex items-center gap-2"
          size="sm"
        >
          <Share2 className="h-4 w-4" />
          Share
        </Button>

        {/* Report Button - Only show if not own profile */}
        {canReport() && (
          <Dialog open={isReportModalOpen} onOpenChange={setIsReportModalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2"
              >
                <Flag className="h-4 w-4" />
                Report User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-800 border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">Report User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">
                    Report Type
                  </label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      <SelectItem value="harassment">Harassment</SelectItem>
                      <SelectItem value="bullying">Bullying</SelectItem>
                      <SelectItem value="inappropriate_behavior">Inappropriate Behavior</SelectItem>
                      <SelectItem value="cheating">Cheating</SelectItem>
                      <SelectItem value="impersonation">Impersonation</SelectItem>
                      <SelectItem value="spam">Spam</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">
                    Description
                  </label>
                  <Textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder="Please provide details about why you're reporting this user..."
                    className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleReport}
                    disabled={isSubmittingReport || !reportType || !reportDescription.trim()}
                    className="bg-red-500 text-white hover:bg-red-600 flex-1"
                  >
                    {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => setIsReportModalOpen(false)}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};